import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Users } from 'lucide-react';
import { Customer } from '../modals/CustomerDetailModal';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useClients } from '@/contexts/ClientsContext';
import DuplicateGroupCard from './DuplicateGroupCard';
import { normalizePhoneForMatch } from '@/lib/validator';

interface DuplicateCustomersViewProps {
    customers: Customer[];
    onMergeSuccess: () => void;
    onSelectionChange?: (hasSelection: boolean) => void;
    onIsMergingChange?: (isMerging: boolean) => void;
    bindTriggerMerge?: (trigger: () => void) => void;
}

// Due clienti sono duplicati solo se condividono un contatto reale (telefono o email).
// Nome e cognome uguali NON bastano: gli omonimi sono persone diverse.
// Logic to find connected components of duplicates
const getDuplicateGroups = (customers: Customer[]): Customer[][] => {
    const parent = new Map<string, string>();
    const find = (i: string): string => {
        if (parent.get(i) === i) return i;
        const root = find(parent.get(i)!);
        parent.set(i, root);
        return root;
    };
    const union = (i: string, j: string) => {
        const rootI = find(i);
        const rootJ = find(j);
        if (rootI !== rootJ) parent.set(rootI, rootJ);
    };

    customers.forEach(c => parent.set(c.id, c.id));

    const phoneMap = new Map<string, string[]>();
    const emailMap = new Map<string, string[]>();

    customers.forEach(c => {
        // Ignora i clienti già "uniti" (se li passiamo per sbaglio qui)
        if (c.merged_into_id) return;

        if (c.phone && c.phone.trim() !== '') {
            const p = normalizePhoneForMatch(c.phone);
            if (p) {
                if (!phoneMap.has(p)) phoneMap.set(p, []);
                phoneMap.get(p)!.push(c.id);
            }
        }
        if (c.email && c.email.trim() !== '') {
            const e = c.email.trim().toLowerCase();
            if (!emailMap.has(e)) emailMap.set(e, []);
            emailMap.get(e)!.push(c.id);
        }
    });

    for (const ids of phoneMap.values()) {
        for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
    }
    for (const ids of emailMap.values()) {
        for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
    }

    const groupsMap = new Map<string, Customer[]>();
    customers.forEach(c => {
        if (c.merged_into_id) return; // Non proporre per il merge
        const root = find(c.id);
        if (!groupsMap.has(root)) groupsMap.set(root, []);
        groupsMap.get(root)!.push(c);
    });

    return Array.from(groupsMap.values()).filter(g => g.length > 1);
};

const DuplicateCustomersView: React.FC<DuplicateCustomersViewProps> = ({
    customers,
    onMergeSuccess,
    onSelectionChange,
    onIsMergingChange,
    bindTriggerMerge
}) => {
    const { t } = useTranslation();
    const { mergeClients } = useClients();

    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set([0]));
    const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null);
    const [isMerging, setIsMerging] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const duplicateGroups = useMemo(() => getDuplicateGroups(customers), [customers]);

    // Sincronizza lo stato di selezione col padre
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedGroupIndex !== null);
        }
    }, [selectedGroupIndex, onSelectionChange]);

    // Sincronizza lo stato di unione col padre
    useEffect(() => {
        if (onIsMergingChange) {
            onIsMergingChange(isMerging);
        }
    }, [isMerging, onIsMergingChange]);

    const toggleGroup = (index: number) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) newSet.delete(index);
            else newSet.add(index);
            return newSet;
        });
    };

    const handleSelectGroup = (index: number) => {
        setSelectedGroupIndex(prev => prev === index ? null : index);
    };

    const handleMerge = async () => {
        if (selectedGroupIndex === null) return;
        setIsConfirmOpen(false);
        const group = duplicateGroups[selectedGroupIndex];
        if (!group || group.length < 2) return;

        setIsMerging(true);
        const ids = group.map(c => c.id);
        const result = await mergeClients(ids);
        setIsMerging(false);

        if (result.success) {
            setSelectedGroupIndex(null);
            onMergeSuccess();
        }
    };

    // Fornisce la funzione di unione al padre
    useEffect(() => {
        if (bindTriggerMerge) {
            bindTriggerMerge(() => setIsConfirmOpen(true));
        }
    }, [bindTriggerMerge]);

    const isEmpty = duplicateGroups.length === 0;

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <Info className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                        <h4 className="text-blue-900 font-bold">{t('customers.duplicates.title')}</h4>
                        <p className="text-blue-800 text-sm mt-1">
                            {t('customers.duplicates.description')}
                        </p>
                    </div>
                </div>
            </div>

            {!isEmpty ? (
                <div className="space-y-4">
                    {duplicateGroups.map((group, index) => (
                        <DuplicateGroupCard
                            key={index}
                            group={group}
                            index={index}
                            isSelected={selectedGroupIndex === index}
                            isExpanded={expandedGroups.has(index)}
                            onSelect={() => handleSelectGroup(index)}
                            onToggleExpand={() => toggleGroup(index)}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-16 rounded-2xl border-2 border-dashed border-gray-300">
                    <Users size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900">{t('customers.duplicates.empty_title')}</h3>
                    <p className="text-gray-500 mt-2">
                        {t('customers.duplicates.empty_description')}
                    </p>
                </div>
            )}

            <ConfirmDialog
                isOpen={isConfirmOpen}
                title={t('customers.duplicates.merge_confirm_title')}
                description={t('customers.duplicates.merge_confirm_desc')}
                confirmLabel={t('customers.duplicates.merge_confirm_btn')}
                onConfirm={handleMerge}
                onCancel={() => setIsConfirmOpen(false)}
                variant="default"
            />
        </div>
    );
};

export default DuplicateCustomersView;
